<template>
  <component
    :is="isNewMultiple ? 'NewMultiselect' : 'vSelect'"
    ref="select"
    v-on="listeners"
    v-model="internalValue"
    v-bind="$attrs"
    :label="label"
    :multiple="multiple"
    :searchable="resolvedSearchable"
    :class="{
      'select--error': error,
      'select--multiple': multiple,
      'select--design-new': design === 'new',
      'select--truncate-selected': design === 'new' && truncateSelected && !multiple
    }"
    class="select"
  >
    <template #option="option">
      <div class="select__item d-center">
        <slot name="custom-option" v-bind="option">{{ displayField ? displayField(option) : option[label] }}</slot>
      </div>
    </template>
    <template #selected-option="option">
      <div
        v-tooltip="isNewMultiple ? { content: displayField ? displayField(option) : option[label], classes: 'tooltip--new' } : null"
        :tabindex="isNewMultiple ? 0 : null"
        class="selected d-center"
      >
        <slot name="custom-selected-option" v-bind="option">
          <slot name="custom-option" v-bind="option">{{ displayField ? displayField(option) : option[label] }}</slot>
        </slot>
      </div>
      <span v-if="isNewMultiple && selectedCount > 1" class="select__count" :aria-label="`${selectedCount - 1} more selected options`">+{{ selectedCount - 1 }}</span>
    </template>
    <template #open-indicator>
      <svg class="select__open-indicator" xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
        <path d="M7.872.418 7.61.138a.423.423 0 0 0-.619 0l-2.99 3.164L1.01.135a.422.422 0 0 0-.619 0L.128.413a.483.483 0 0 0 0 .655L3.691 4.85a.437.437 0 0 0 .31.149h.002a.437.437 0 0 0 .31-.149l3.56-3.773A.482.482 0 0 0 8 .747a.477.477 0 0 0-.128-.33Z" fill="#606060"/>
      </svg>
    </template>
    <template #no-options="{ search, searching }">
      <slot name="no-options" :search="search" :searching="searching">
        <div v-if="searching" class="select__noty">No results for your search <em>{{ search }}</em>. Please try again.</div>
        <div v-else class="select__noty">No Results Found</div>
      </slot>
    </template>
    <template #list-footer><slot name="list-footer" /></template>
  </component>
</template>

<script>
import vSelect from 'vue-select'

// Reorder the displayed list, not the options prop: selection must not trigger resetOnOptionsChange.
const NewMultiselect = {
  extends: vSelect,
  props: {
    deselectFromDropdown: { type: Boolean, default: true }
  },
  computed: {
    filteredOptions() {
      const options = vSelect.computed.filteredOptions.call(this)
      const selected = []
      const remaining = []
      options.forEach(option => {
        if (this.isOptionSelected(option)) selected.push(option)
        else remaining.push(option)
      })
      return selected.concat(remaining)
    }
  }
}

export default {
  name: 'AppSelect',
  components: {vSelect, NewMultiselect},
  inheritAttrs: false,
  props: {
    value: null,
    error: {
      type: Boolean,
      default: false
    },
    displayField: {
      type: Function,
    },
    label: {
      type: String,
      default: 'name'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    truncateSelected: {
      type: Boolean,
      default: false
    },
    design: {
      type: String,
      default: 'legacy',
      validator: value => ['legacy', 'new'].includes(value)
    },
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      internalValue: this.value
    }
  },
  computed: {
    resolvedSearchable() {
      const { searchable, options, filterable, taggable } = this.$attrs
      if (searchable !== undefined) return searchable
      if (this.design !== 'new') return true
      // Remote lookup and option creation require input even before options exist.
      if (this.$listeners.search || filterable === false || taggable === '' || taggable) return true
      return Array.isArray(options) && options.length >= 5
    },
    isNewMultiple() {
      return this.design === 'new' && this.multiple
    },
    selectedCount() {
      return Array.isArray(this.internalValue) ? this.internalValue.length : 0
    },
    listeners() {
      const { input, ...listeners } = this.$listeners
      return listeners
    }
  },
  watch: {
    resolvedSearchable(searchable) {
      if (this.design === 'new' && this.$attrs.searchable === undefined && !searchable && this.$refs.select) {
        this.$refs.select.search = ''
      }
    },
    ariaLabel() {
      this.syncAriaLabel()
    },
    value(newValue) {
      if (newValue !== this.internalValue) this.internalValue = newValue
    },
    internalValue(newValue) {
      if (newValue !== this.value) this.$emit('input', newValue)
    }
  },
  mounted() {
    this.syncAriaLabel()
  },
  updated() {
    this.syncAriaLabel()
  },
  methods: {
    syncAriaLabel() {
      if (!this.ariaLabel || !this.$refs.select) return
      const combobox = this.$refs.select.$el.querySelector('[role="combobox"]')
      if (combobox) combobox.setAttribute('aria-label', this.ariaLabel)
    }
  }
}
</script>

<style lang="scss">
@import "vue-select/src/scss/vue-select.scss";

.select {
  .vs__search {
    font-family: $font-family-base;
    font-size: 13px;
    font-weight: 400;
    color: $color-text-base;
    line-height: 1.5;
  }

  .vs__search::placeholder {
    font-family: $font-family-base;
    font-size: 13px;
    font-weight: 400;
    color: #818181;
  }

  .vs__dropdown-toggle {
    position: relative;
    height: 38px;
    padding: 0 0 0 5px;
    border-color: $color-border;
    border-radius: 6px;
    background-color: #fff;
    overflow: hidden;
  }

  img {
    height: auto;
    max-width: 2.5rem;
    margin-right: 4px;
  }

  .d-center {
    display: flex;
    align-items: center;
  }

  .selected {
    white-space: nowrap;
    overflow: hidden;
  }

  .selected .special {
    margin-left: 5px;
  }

  .select__item {
    padding: 3px 0;
    color: $color-text-base;
    font-size: 13px;
    font-weight: 400;
    text-decoration: none;
  }

  .select__noty {
    padding: 3px 15px 4px;
    font-size: 13px;

    span, em {
      font-weight: 600;
    }
  }

  .vs__dropdown-option {
    padding: 2px 12px;

    .select__item {
      color: $color-text-second;
    }

    &.vs__dropdown-option--disabled:not(.vs__dropdown-option--selected) {
      .select__item {
        opacity: 0.5;
      }
    }

    &.vs__dropdown-option--disabled {
      cursor: not-allowed;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__selected {
    position: absolute;
    top: 8px;
    margin-top: 0;
    font-family: $font-family-base;
    font-size: 13px;
    color: $color-text-base;
    overflow: hidden;
  }

  .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
    margin-right: 20px;
  }

  .vs__selected .select__img {
    width: auto;
    max-height: 20px;
    margin-right: 4px;
  }

  .v-select .dropdown li {
    border-bottom: 1px solid rgba(112, 128, 144, 0.1);
  }

  .v-select .dropdown li:last-child {
    border-bottom: none;
  }

  .v-select .dropdown li a {
    padding: 10px 20px;
    width: 100%;
    font-size: 13px;
    color: #3c3c3c;
  }

  .vs__clear {
    margin-right: 0;
    line-height: 0;
  }

  .v-select .dropdown-menu .active > a {
    color: #fff;
  }

  .vs__search, .vs__search:focus {
    margin: 0;
  }

  &.vs--open .vs__actions > svg {
    transform: rotate(180deg);
  }

  &.vs--open.vs--single .vs__selected {
    top: 8px;
  }

  &.vs--open .vs__dropdown-toggle {
    border-bottom-color: $color-border;
    border-radius: 7px 7px 0 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.vs--open .select__open-indicator path {
    stroke: $color-theme
  }

  &.vs--loading .open-indicator {
    opacity: 0;
  }

  &.vs--loading img, &.vs--loading svg {
    display: none;
  }

  &[readonly] {
    pointer-events: none;

    .vs__dropdown-toggle {
      border-color: #ff9416;
      padding: 0;
    }

    .vs__actions {
      display: none;
    }

    .vs__deselect {
      display: none;
    }

    .vs__selected {
      padding: 1px 10px;
    }
  }

  .open-indicator {
    transition: transform 0.3s;
  }

  .vs__spinner {
    position: absolute;
    left: 6px;
    top: 6px;
    border-color: rgba($color-theme, 0.2);
    border-left-color: rgba($color-theme, 0.8);
  }

  .vs__dropdown-menu {
    top: 100%;
    max-height: 174px;
    padding-top: 0;
    padding-bottom: 0;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 7px 7px;
    border-width: 0 1px 1px 1px;
    border-style: solid;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: $color-theme transparent;
    scrollbar-width: thin;

    &::after {
      content: "";
      margin-bottom: 5px;
    }

    li {
      white-space: normal!important;

      &.vs__no-options {
        padding: 10px;
        font-size: 13px;
      }
    }
  }

  .vs__dropdown-menu::-webkit-scrollbar {
    width: 5px;
  }

  .vs__dropdown-menu::-webkit-scrollbar-track {
    border-radius: 5px;
    background-color: transparent;
  }

  .vs__dropdown-menu::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background-color: $color-theme;
  }

  .vs__dropdown-menu li {
    overflow-wrap: anywhere;
    white-space: break-spaces;
  }

  .vs__actions {
    flex-shrink: 0;
    width: 38px;
    position: relative;
    padding: 0 14px 0 15px;
    background-color: #fafafa;
    border-left: 1px solid $color-border;
  }

  .vs__clear svg {
    display: none;
  }

  .vs__clear::after {
    content: '';
    display: block;
    position: absolute;
    right: 48px;
    top: 50%;
    width: 8px;
    height: 8px;
    background-image: url("~@/assets/img/cancel-icon.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transform: translateY(-50%);
  }

  .select-item {
    padding: 10px 0;
    font-size: 13px;
    font-weight: 600;
    color: $color-text-base;

    &:hover {
      background-color: $color-border;
    }
  }

  &.drop-up {
    &.vs--open {
      .vs__dropdown-toggle {
        border-radius: 0 0 6px 6px;
        //border-bottom-color: $color-theme;
        border-top-color: #c7cfdd;
      }
    }
  }

  &.select--small {
    min-width: 100px;

    .vs__dropdown-menu {
      min-width: auto;
    }
  }
}

.select--notminw {
  min-width: 154px;
}
.select--grow1 {
  flex-grow: 1;
}
.select--200 {
  min-width: 200px;
}
.select--180 {
  min-width: 180px;
}

.select--160 {
  min-width: 160px;
}

.select--100 {
  min-width: 100px;
}

.select--max-200 {
  max-width: 200px;
  margin: 0 auto;
}

.select--max-250 {
  max-width: 250px;
  margin: 0 auto;
}

.select--error {
  .vs__dropdown-toggle {
    border-color: $color-error;
  }

  .open-indicator circle {
    fill: $color-error;
  }
}

.select--multiple {
  .vs__dropdown-toggle {
    height: auto;
    min-height: 38px;
  }

  .vs__selected-options {
    flex-wrap: wrap;
    align-items: center;
    padding: 3px 2px;
    overflow: visible;
  }

  .vs__selected {
    position: static;
    margin: 2px;
    padding: 3px 6px 3px 10px;
    border-radius: 20px;
    border: 1px solid $color-border;
    background-color: transparent;
    color: $color-text-base;
    font-size: 13px;
    font-weight: 400;
  }

  .vs__deselect {
    margin-left: 4px;
    padding: 4px;
    border-radius: 50%;

    svg {
      transform: scale(0.8);

      path {
        fill: #8a8a8a;
      }
    }
  }

  .selected {
    white-space: break-spaces;
  }
}

.select--maxw {
  width: 300px;
  margin: 0 auto;

  @media(max-width: 550px) {
    min-width: 100%;
    width: auto;
  }
}

.select--250 {
  width: 250px;
}

.select--300 {
  width: 300px;

  @media(max-width: 550px) {
    min-width: 100%;
    width: auto;
  }
}

.select--minw {
  min-width: 450px;

  @media(max-width: 550px) {
    min-width: 100%;
  }
}

.vs__dropdown-menu::-webkit-scrollbar {
  width: 2px;
}

.vs__dropdown-menu::-webkit-scrollbar-track {
  border-radius: 5px;
  background-color: transparent;
}

.vs__dropdown-menu::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: $color-theme;
}

.vs__dropdown-menu[data-popper-placement="bottom"] {
  top: 100%;
  max-height: 174px;
  padding-top: 0;
  padding-bottom: 0;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 7px 7px;
  border-width: 0 1px 1px 1px;
  border-style: solid;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: $color-theme transparent;
  scrollbar-width: thin;

  &::after {
    content: "";
    margin-bottom: 5px;
  }

  li {
    white-space: normal!important;

    &.vs__no-options {
      padding: 10px;
      font-size: 13px;
    }
  }

  .vs__dropdown-option {
    padding: 4px 12px;

    .select__item {
      color: $color-text-second;
      font-size: 13px;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }
}

.vs__dropdown-menu[data-popper-placement="top"] {
  top: 100%;
  max-height: 174px;
  padding-top: 0;
  padding-bottom: 0;
  background-color: #fff;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 7px 7px 0 0;
  border-width: 1px 1px 0 1px;
  border-style: solid;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: $color-theme transparent;
  scrollbar-width: thin;

  &::after {
    content: "";
    margin-bottom: 5px;
  }

  li {
    white-space: normal!important;

    &.vs__no-options {
      padding: 10px;
      font-size: 13px;
    }
  }

  .vs__dropdown-option {
    padding: 4px 12px;

    .select__item {
      color: $color-text-second;
      font-size: 13px;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }
}

.select.vs--disabled .vs__search {
  background-color: #fff;
}

.family.select {
  .vs__selected {
    color: #000;
    font-family: 'CoreSansCR', sans-serif;
    font-size: 14px;
    font-weight: 400;
  }

  .vs__search {
    color: #000;
    font-family: 'CoreSansCR', sans-serif;
    font-size: 14px;
    font-weight: 400;
  }

  .vs__search::placeholder {
    color: #707070;
    font-family: 'CoreSansCR', sans-serif;
    font-size: 14px;
    font-weight: 400;
  }

  .vs__dropdown-menu {
    color: #000;
    font-family: 'CoreSansCR', sans-serif;
  }

  &[readonly] {
    pointer-events: none;

    .vs__dropdown-toggle {
      padding: 0 0 0 5px;
    }
  }
}

.select--design-new {
  font-family: $ui-font-body;

  .vs__spinner {
    border-color: rgba($ui-blue, .2);
    border-left-color: $ui-blue;
  }

  .vs__dropdown-toggle {
    min-height: $ui-control-height;
    height: $ui-control-height;
    padding-left: $ui-space-8;
    border-color: $ui-line-strong;
    border-radius: $ui-control-radius;
    background-color: $ui-surface;
    transition: border-color .2s ease;
  }

  .vs__search,
  .vs__search:focus,
  .vs__selected,
  .select__item {
    color: $ui-text;
    font-family: $ui-font-body;
    font-size: $ui-font-size-body;
  }

  .vs__search::placeholder {
    color: $ui-subtle;
    font-family: $ui-font-body;
    font-size: $ui-font-size-body;
  }

  .vs__selected {
    top: 9px;
  }

  .vs__actions {
    display: flex;
    width: $ui-control-height;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-left-color: $ui-line;
    background-color: $ui-surface-soft;
  }

  .select__open-indicator {
    display: block;
    flex: 0 0 auto;
    margin: 0;
    transform-origin: center;
  }

  .vs__dropdown-menu {
    padding: $ui-space-4 0;
    border: 1px solid $ui-line-strong;
    border-radius: 0 0 $ui-control-radius $ui-control-radius;
    background-color: $ui-surface;
    box-shadow: none;
    scrollbar-color: var(--ui-accent, #{$ui-blue}) transparent;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $ui-control-radius;
      background-color: var(--ui-accent, #{$ui-blue});
    }
  }

  .vs__dropdown-option {
    padding: $ui-space-8 $ui-space-12;

    &.vs__dropdown-option--highlight {
      background-color: var(--ui-accent-soft, #{$ui-blue-soft});

      .select__item {
        color: var(--ui-accent, #{$ui-blue});
      }
    }
  }

  &.vs--open {
    position: relative;
    z-index: 10;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: var(--ui-accent, #{$ui-blue});
    box-shadow: none;
  }

  &.vs--open .vs__dropdown-menu {
    border-color: var(--ui-accent, #{$ui-blue});
    border-top-width: 0;
    box-shadow: none;
  }

  &.vs--open .select__open-indicator path {
    stroke: none;
  }

  &.select--truncate-selected {
    .vs__selected-options { min-width: 0; flex-wrap: nowrap; }
    .vs__selected { flex: 0 1 auto; min-width: 0; max-width: 100%; }
    .selected { display: block; min-width: 0; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .vs__search { min-width: 0; }
  }

  &.select--multiple {
    .vs__dropdown-toggle {
      height: $ui-control-height;
      min-height: $ui-control-height;
    }

    .vs__selected-options {
      min-width: 0;
      margin-right: $ui-space-8;
      flex-wrap: nowrap;
      overflow: hidden;
      padding: 0;
    }

    .vs__selected {
      display: none;
      flex: 0 1 auto;
      min-width: 0;
      max-width: 100%;
      height: 28px;
      margin: 0;
      padding: 0 $ui-space-8;
      border-color: $ui-line-strong;
      border-radius: $ui-control-radius;
      background-color: $ui-surface-soft;

      &:first-of-type { display: flex; }
    }

    .selected {
      display: block;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      > * { min-width: 0; max-width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
      &:focus-visible { outline: 2px solid $ui-blue; outline-offset: -2px; }
    }

    .select__count {
      flex: 0 0 auto;
      margin-left: $ui-space-4;
      padding: 0 $ui-space-4;
      border-radius: $ui-space-4;
      background: $ui-line;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
      line-height: 20px;
      white-space: nowrap;
    }

    .vs__deselect { flex: 0 0 auto; }
    .vs__search { flex: 1 1 24px; min-width: 24px; }

    &.vs--searching .vs__selected { display: none; }
    &.vs--searching .vs__search { flex-basis: 100%; }

    &.vs--disabled .vs__deselect {
      cursor: not-allowed;
    }

    .vs__dropdown-option.vs__dropdown-option--selected {
      background-color: $ui-blue-soft;
      .select__item { color: $ui-blue; }
    }
  }

  &.select--error .vs__dropdown-toggle,
  &.vs--open.select--error .vs__dropdown-menu {
    border-color: $ui-red;
  }

  &[readonly] .vs__dropdown-toggle,
  &.vs--disabled .vs__dropdown-toggle {
    border-color: $ui-line;
    background-color: $ui-surface-soft;
    opacity: .72;
  }

  &.select.vs--disabled .vs__search {
    background-color: transparent;
  }
}
</style>
